import React from 'react';
import { useToast, type Toast } from '../../contexts/ToastContext';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';

const VARIANT_ACCENTS: Record<Toast['variant'], string> = {
  success: '#4ADE80',
  error: '#EF4444',
  info: '#01A9ED',
};

const VARIANT_ICONS: Record<Toast['variant'], string> = {
  success: '✓',
  error: '✕',
  info: 'i',
};

/**
 * Global toast rack anchored bottom-right. Stacks multiple toasts with a
 * small gap. Each toast animates in and auto-dismisses if `timeoutMs` is set.
 */
export function ToastContainer(): React.JSX.Element | null {
  const { toasts, dismiss } = useToast();
  const { isDark } = useTheme();
  if (toasts.length === 0) return null;

  return (
    <div
      className="vx-toast-rack"
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      {toasts.map(toast => (
        <ToastCard key={toast.id} toast={toast} isDark={isDark} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}

interface ToastCardProps {
  toast: Toast;
  isDark: boolean;
  onDismiss: () => void;
}

/** One toast bubble. Clickable when `href` is set. */
function ToastCard({ toast, isDark, onDismiss }: ToastCardProps): React.JSX.Element {
  const accent = VARIANT_ACCENTS[toast.variant];
  const colors = isDark ? COLORS.dark : COLORS.light;

  const body = (
    <div
      className="vx-toast-card"
      style={{
        minWidth: '320px',
        maxWidth: '420px',
        padding: '12px 14px',
        borderRadius: '10px',
        backgroundColor: colors.card,
        border: `1px solid ${accent}55`,
        boxShadow: '0 12px 32px rgba(0,0,0,0.32)',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        pointerEvents: 'auto',
        animation: 'toastIn 180ms ease-out',
      }}
    >
      <ToastIcon variant={toast.variant} accent={accent} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: accent }}>
          {toast.title}
        </div>
        {toast.body && (
          <div style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.text, marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {toast.body}
          </div>
        )}
      </div>
      <button
        onClick={e => { e.stopPropagation(); e.preventDefault(); onDismiss(); }}
        aria-label="Dismiss"
        style={{ border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer', fontSize: '14px' }}
      >
        ×
      </button>
    </div>
  );

  if (toast.href) {
    return (
      <a
        href={toast.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
        onClick={onDismiss}
      >
        {body}
      </a>
    );
  }
  return body;
}

/** Small colored circle with the variant glyph inside. */
function ToastIcon({ variant, accent }: { variant: Toast['variant']; accent: string }): React.JSX.Element {
  return (
    <div
      style={{
        flexShrink: 0,
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: `${accent}22`,
        color: accent,
        fontFamily: FONTS.heading,
        fontSize: '12px',
        display: 'grid',
        placeItems: 'center',
        border: `1px solid ${accent}55`,
      }}
    >
      {VARIANT_ICONS[variant]}
    </div>
  );
}
